// RAIL_PICKUP / RAIL_DELIVERY are intentionally NOT here — they are
// carry-through rail legs (goods stay in the same containers, customer never
// involved) handled like TRANSIT via the self-loop path below.
const LOADING_EVENT_TYPES = ['PICKUP', 'PACKING', 'PET_PICKUP', 'VEHICLE_PICKUP', 'VEHICLE_DEPOT_PICKUP'];
const UNLOADING_EVENT_TYPES = ['DELIVERY', 'UNPACKING', 'PET_DELIVERY', 'VEHICLE_DELIVERY', 'VEHICLE_DEPOT_DELIVERY'];
const STORAGE_IN_EVENT_TYPES = ['STORAGE'];
const STORAGE_OUT_EVENT_TYPES = ['END_STORAGE'];
const WHOLE_MOVE_EVENT_TYPES = ['WHOLE_MOVE'];

export function buildCapacityFlows(item, remainingResources, resourceType, removedResourceId) {
    const eventType = item.action;

    if (LOADING_EVENT_TYPES.includes(eventType)) {
        return remainingResources.map((r) => ({
            source_type: 'customer',
            source_id: null,
            destination_type: resourceType,
            destination_id: r.id,
            capacity: r.allocated_capacity || r.allocation || 0,
        }));
    }

    if (UNLOADING_EVENT_TYPES.includes(eventType)) {
        return remainingResources.map((r) => ({
            source_type: resourceType,
            source_id: r.id,
            destination_type: 'customer',
            destination_id: null,
            capacity: r.allocated_capacity || r.allocation || 0,
        }));
    }

    if (STORAGE_IN_EVENT_TYPES.includes(eventType)) {
        return remainingResources.map((r) => ({
            source_type: resourceType,
            source_id: r.id,
            destination_type: 'storage',
            destination_id: null,
            capacity: r.allocated_capacity || r.allocation || 0,
        }));
    }

    if (STORAGE_OUT_EVENT_TYPES.includes(eventType)) {
        return remainingResources.map((r) => ({
            source_type: 'storage',
            source_id: null,
            destination_type: resourceType,
            destination_id: r.id,
            capacity: r.allocated_capacity || r.allocation || 0,
        }));
    }

    if (WHOLE_MOVE_EVENT_TYPES.includes(eventType)) {
        const flows = [];
        for (const r of remainingResources) {
            const cap = r.allocated_capacity || r.allocation || 0;
            flows.push({
                source_type: 'customer',
                source_id: null,
                destination_type: resourceType,
                destination_id: r.id,
                capacity: cap,
            });
            flows.push({
                source_type: resourceType,
                source_id: r.id,
                destination_type: 'customer',
                destination_id: null,
                capacity: cap,
            });
        }
        return flows;
    }

    const filtered = (item.capacity_flows || []).reduce((acc, f) => {
        const isSource = f.source_type === resourceType && f.source_id;
        const isDest = f.destination_type === resourceType && f.destination_id;
        if (!isSource && !isDest) {
            return acc;
        }
        if (isSource && f.source_id === removedResourceId) {
            return acc;
        }
        if (isDest && f.destination_id === removedResourceId) {
            if (isSource) {
                acc.push({
                    ...f,
                    destination_type: 'none',
                    destination_id: null,
                });
            }
            return acc;
        }
        acc.push(f);
        return acc;
    }, []);

    // Self-loop semantics: source = destination on the same resource. Valid
    // only on resource-to-resource events (TRANSIT, TRANSFER, MOVEMENT,
    // YARD_WORK) — operator-confirmed "just driving" / carryover bookkeeping.
    // Any other event type with a self-loop is invalid — the API rejects it.
    // Previously this filter kept self-loops whenever any non-self-loop also
    // existed; that was permissive on PICKUP/etc. and produced legacy bad
    // rows on demo. Tighten to resource-to-resource only here so the CRM
    // stops generating them on loading/unloading/storage events. See §3.14.
    const allowsSelfLoop = ['TRANSIT', 'TRANSFER', 'MOVEMENT', 'YARD_WORK', 'RAIL_PICKUP', 'RAIL_DELIVERY'].includes(
        eventType,
    );

    const finalFlows =
        allowsSelfLoop ? filtered : (
            filtered.filter(
                (f) =>
                    !(
                        f.source_type === resourceType &&
                        f.destination_type === resourceType &&
                        f.source_id === f.destination_id
                    ),
            )
        );

    return finalFlows.map((f) => ({
        source_type: f.source_type,
        source_id: f.source_id,
        destination_type: f.destination_type,
        destination_id: f.destination_id,
        capacity: f.capacity,
    }));
}
